import { type TwoFactorAuthType } from '@pkg/types';

import { type EmojiId } from '../emoji/emoji';
import { type Roles } from '../user/roles';

export interface LootBoxType {
    // The loot box type id
    id?: LootBoxTypeId;
    // The name of this loot box type
    name: string;
    // A more detailed description of the loot box type
    description: string;
    /// The loot box type's configuration parameters
    config: LootBoxTypeConfig;
    /// The timestamp for when this loot box type was created
    created_at?: string;
}

export interface LootBoxTypeConfig {
    // The number of yats in the loot box
    size: number;
    // The lower bound (inclusive) rhythm score for standard yats in the loot box
    min_base_score: number;
    // The upper bound (exclusive) rhythm score for standard yats in the loot box
    max_base_score: number;
    // A set of guaranteed drops in this loot box type
    guarantees: Guarantee[];
    // A set of probabilty weightings for chance-based drops
    weights: Weight[];
    min_length?: 1;
    max_length?: 5;
}

export interface Guarantee {
    // The number of guaranteed drops of this type in the loot box
    count: number;
    // The lowest (inclusive) rhythm score range for guaranteed drop
    min_score: number;
    // The highest (exclusive) rhythm score range for guaranteed drop
    max_score: number;
}

export interface Weight {
    // The lowest (inclusive) rhythm score range for inclusion when the probability spec hits
    min_score: number;
    // The highest (exclusive) rhythm score range for inclusion when the probability spec hits
    max_score: number;
    // The inverse probability ratio. This is a 1:n value. i.e. an ipr of 5 means a 1 in 5 chance of occurring, or 20%
    ipr: number;
}

export type LootBoxTypeId = string;
export type LootBoxId = string;

export interface LootBox {
    id: LootBoxId;
    lootbox_type_id: LootBoxTypeId;
    yats: string[];
    scores: number[];
    prices: number[];
    total_value: number;
    owner_id: string;
    average_rhythm_score: number;
    created_at: string;
    status: LootBoxStatus;
}

export enum LootBoxStatus {
    Available = 'Available',
    Owned = 'Owned',
    Draft = 'Draft',
    Used = 'Used',
}

export interface GenerateLootBoxesRequest {
    loot_box_type_id: LootBoxTypeId;
    num_boxes: number;
    status: LootBoxStatus;
}
export interface UpdateLootBoxesRequest {
    yats?: string[];
    status?: LootBoxStatus;
    owner_email?: string;
}

export interface RequestLootBoxOpeningResponse {
    id: LootBoxId;
    status: LootBoxStatus;
    owner_id: string;
    yats: string[];
    scores: number[];
    prices: number[];
    average_rhythm_score: number;
    total_value: number;
    requested_opening_at: string;
    created_at: string;
}

export type RemoveOpeningResponse = RequestLootBoxOpeningResponse;

export interface OpeningRequestsResponseData {
    id: LootBoxId;
    lootbox_type_id: LootBoxTypeId;
    lootbox_type: {
        id: LootBoxTypeId;
        name: string;
        description: string;
        config: LootBoxTypeConfig;
        created_at: string;
    };
    status: LootBoxStatus;
    owner_id: string;
    owner: {
        id: string;
        email: string;
        first_name?: string;
        last_name?: string;
        role: Roles;
        two_factor_auth?: null | TwoFactorAuthType[];
        two_factor_last_prompted_at?: string;
        two_factor_should_prompt: false;
        free_limit: number;
        remaining_free_emoji: number;
        source: null;
        pubkeys: string[];
        email_verified_at: string;
        created_at: string;
        updated_at: string;
    };
    yats: EmojiId[];
    scores: number[];
    prices: number[];
    average_rhythm_score: number;
    total_value: number;
    requested_opening_at: string;
    created_at: string;
}
